import { useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Phone, User, AlertTriangle } from '../icons/wp-icons';

const ENTER_DELAY_MS = 3000;
const LEAVE_DELAY_MS = 300;
const POPUP_WIDTH = 360;
const POPUP_MAX_HEIGHT = 280;

function formatDate(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('de-DE', {
      day: '2-digit', month: '2-digit', year: '2-digit',
      hour: '2-digit', minute: '2-digit'
    });
  } catch {
    return '';
  }
}

export default function AnfrageHoverPreview({ anfrage, children, className }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const enterTimer = useRef(null);
  const leaveTimer = useRef(null);
  // Letzte Mausposition tracken, damit das Popup nach 3 s dort erscheint,
  // wo der Cursor TATSÄCHLICH steht — nicht dort wo er die Karte betreten hat.
  const lastMouse = useRef({ x: 0, y: 0 });
  const isMobile = typeof window !== 'undefined' && (window.innerWidth <= 768 || 'ontouchstart' in window);

  const clearTimers = () => {
    if (enterTimer.current) { clearTimeout(enterTimer.current); enterTimer.current = null; }
    if (leaveTimer.current) { clearTimeout(leaveTimer.current); leaveTimer.current = null; }
  };

  const computePosition = useCallback((mouseX, mouseY) => {
    let left = mouseX + 16;
    let top = mouseY + 16;
    if (left + POPUP_WIDTH > window.innerWidth - 12) left = mouseX - POPUP_WIDTH - 16;
    if (top + POPUP_MAX_HEIGHT > window.innerHeight - 12) top = mouseY - POPUP_MAX_HEIGHT - 16;
    if (left < 8) left = 8;
    if (top < 8) top = 8;
    return { top, left };
  }, []);

  const handleMouseEnter = (e) => {
    if (isMobile) return;
    lastMouse.current = { x: e.clientX, y: e.clientY };
    clearTimers();
    enterTimer.current = setTimeout(() => {
      setPos(computePosition(lastMouse.current.x, lastMouse.current.y));
      setOpen(true);
    }, ENTER_DELAY_MS);
  };

  const handleMouseMove = (e) => {
    if (isMobile) return;
    // Nur tracken solange das Popup noch NICHT offen ist — sobald es steht,
    // soll es nicht mit dem Cursor mitwandern (Chase-Loop-Vermeidung).
    if (!open) {
      lastMouse.current = { x: e.clientX, y: e.clientY };
    }
  };

  const handleMouseLeave = () => {
    if (isMobile) return;
    if (enterTimer.current) { clearTimeout(enterTimer.current); enterTimer.current = null; }
    leaveTimer.current = setTimeout(() => setOpen(false), LEAVE_DELAY_MS);
  };

  const handlePopupEnter = () => {
    if (leaveTimer.current) { clearTimeout(leaveTimer.current); leaveTimer.current = null; }
  };
  const handlePopupLeave = () => setOpen(false);

  if (!anfrage) return children;

  const summary = (anfrage.nachricht || '').replace(/\s+/g, ' ').trim();
  const trimmedBetreff = (anfrage.betreff || '').replace(/^🚨\s*NOTFALL\s*—\s*/i, '').trim() || 'Anruf';

  // Das Popup MUSS per Portal ans document.body — sonst klebt es an
  // einem transformierten Vorfahr (z.B. react-grid-layout setzt
  // `transform: translate(...)` auf jedes Dashboard-Widget, wodurch
  // `position: fixed` relativ zu dieser Box bindet statt zum Viewport).
  const popupNode = open ? (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, scale: 0.96, y: 4 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.97 }}
        transition={{ duration: 0.18 }}
        onMouseEnter={handlePopupEnter}
        onMouseLeave={handlePopupLeave}
        className="fixed z-[9999] bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden pointer-events-auto"
        style={{
          top: pos.top,
          left: pos.left,
          width: `${POPUP_WIDTH}px`,
          maxHeight: `${POPUP_MAX_HEIGHT}px`
        }}
      >
            <div className={`px-3 py-2 border-b border-gray-100 flex items-center gap-2 ${
              anfrage.istNotfall ? 'bg-red-50' : 'bg-gray-50'
            }`}>
              {anfrage.istNotfall ? (
                <AlertTriangle className="w-4 h-4 text-red-600 shrink-0" />
              ) : (
                <Phone className="w-4 h-4 text-gray-500 shrink-0" />
              )}
              <p className={`text-sm font-semibold truncate ${
                anfrage.istNotfall ? 'text-red-700' : 'text-gray-900'
              }`}>
                {anfrage.istNotfall && (
                  <span className="text-[10px] font-bold uppercase tracking-wider mr-1.5 px-1.5 py-0.5 bg-red-600 text-white rounded">
                    Notdienst
                  </span>
                )}
                {trimmedBetreff}
              </p>
            </div>
            <div className="px-3 py-2 space-y-1.5">
              {anfrage.kunde?.name && (
                <p className="text-xs text-gray-600 flex items-center gap-1.5">
                  <User className="w-3 h-3 text-gray-400 shrink-0" />
                  <span className="truncate">{anfrage.kunde.name}</span>
                </p>
              )}
              <p className="text-[11px] text-gray-400">{formatDate(anfrage.erstelltAm)}</p>
              {summary ? (
                <p className="text-xs text-gray-700 leading-relaxed whitespace-pre-line"
                   style={{ maxHeight: '170px', overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 8, WebkitBoxOrient: 'vertical' }}>
                  {summary}
                </p>
              ) : (
                <p className="text-xs text-gray-400 italic">Keine Zusammenfassung verfügbar</p>
              )}
            </div>
      </motion.div>
    </AnimatePresence>
  ) : null;

  return (
    <div
      onMouseEnter={handleMouseEnter}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      className={className}
    >
      {children}
      {typeof document !== 'undefined' && popupNode
        ? createPortal(popupNode, document.body)
        : null}
    </div>
  );
}
